import React, { useState, useEffect, useRef } from 'react';
import { useHistory, Link } from 'react-router-dom';
import api from '../../api';
import { toast } from 'react-toastify';

/* ====================================================================
   1. INTERNAL COMPONENT: PRODUCT CARD 
   (The "Our Shop" Design adapted for Home Page Slider)
   ==================================================================== */
const ProductCard = ({ product }) => {
  const history = useHistory();
  const [quantity, setQuantity] = useState(1);
  const [selectedPack, setSelectedPack] = useState(1);
  const [adding, setAdding] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);

  // --- Logic A: Data Normalization ---
  const productId = product.productId || product.id;
  const price = Number(product.productPrice || product.price || 0);
  const mrp = Number(product.productMRP || product.mrp || product.MRP || 0);
  const name = product.productName || "Unknown Product";
  const category = product.category || "General";

  // Dynamic Pack Size Logic
  const maxPackSize = product.productQuantity ? parseInt(product.productQuantity) : 3;
  const packs = Array.from({ length: maxPackSize > 0 ? maxPackSize : 3 }, (_, i) => i + 1);

  // Image handling
  const image = product.productPhotos && product.productPhotos.length > 0
      ? product.productPhotos[0].photoName || product.productPhotos[0]
      : "https://via.placeholder.com/300?text=No+Image";

  // Price Calculations
  const currentPrice = price * selectedPack;
  const currentMRP = mrp * selectedPack;

  const increment = () => setQuantity(q => q + 1);
  const decrement = () => setQuantity(q => (q > 1 ? q - 1 : 1));

  // --- Logic A: Wishlist & Cart Handlers ---
  useEffect(() => {
    const stored = localStorage.getItem("wishlist");
    if (stored) {
      const list = JSON.parse(stored);
      if (list.find(item => item.productId === productId)) setInWishlist(true);
    }
  }, [productId]);

  const toggleWishlist = () => {
    let list = [];
    const stored = localStorage.getItem("wishlist");
    if (stored) list = JSON.parse(stored);

    if (inWishlist) {
      list = list.filter(item => item.productId !== productId);
      setInWishlist(false);
      toast.info("Removed from Wishlist");
    } else {
      list.push({ productId, productName: name, productPrice: price, image });
      setInWishlist(true);
      toast.success("Added to Wishlist");
    }
    localStorage.setItem("wishlist", JSON.stringify(list));
    setInWishlist(!inWishlist);
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  const handleAddToCart = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "0") {
      toast.info("Please Log in to add items!");
      history.push("/login");
      return;
    }
    try {
      setAdding(true);
      const finalQty = quantity * selectedPack;
      await api.post('/usercart/add-product', null, {
        params: { userId, productId, orderQuantity: finalQty }
      });
      toast.success(`Added ${finalQty} ${name}(s) to cart`);
    } catch (error) {
      console.error("Cart Error:", error);
      toast.error("Failed to add item");
    } finally {
      setAdding(false);
    }
  };

  return (
    // 🟢 CRITICAL CHANGE: 
    // Replaced 'col-lg-4...' with slider specific styles so it scrolls correctly
    <div style={{ minWidth: '300px', maxWidth: '300px', margin: '0 15px', flex: '0 0 auto' }}>
      
      <div className="card h-100 shadow-sm" style={{ borderRadius: '15px', border: '1px solid #eee', overflow: 'hidden', backgroundColor: '#fff' }}>
        
        {/* Wishlist Button */}
        <button onClick={toggleWishlist} style={{
            position: 'absolute', top: '15px', right: '15px', background: 'white', border: 'none',
            borderRadius: '50%', width: '35px', height: '35px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
          }}>
           <span style={{ fontSize: '20px', color: inWishlist ? 'red' : '#ccc', transition: '0.2s' }}>
             {inWishlist ? '♥' : '♡'}
           </span>
        </button>

        <Link to={`/product/${productId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          
          {/* IMAGE CONTAINER */}
          <div style={{ 
              textAlign: 'center', padding: '0', backgroundColor: '#fff', 
              height: '280px', width: '100%', display:'flex', 
              alignItems:'center', justifyContent:'center', borderBottom: '1px solid #f9f9f9', overflow: 'hidden' 
          }}>
            <img src={image} alt={name} 
              onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
              style={{ height: '100%', width: '100%', objectFit: 'cover', objectPosition: 'center' }}
            />
          </div>

          {/* CARD BODY */}
          <div className="p-3">
            <span className="badge bg-light text-muted border mb-2" style={{fontSize: '11px', fontWeight: '500'}}>{category}</span>
            <h6 style={{ 
                fontWeight: '700', fontSize: '16px', marginBottom: '5px', minHeight: '44px', 
                lineHeight: '1.4', overflow:'hidden', textOverflow:'ellipsis', 
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' 
            }}>
                {name}
            </h6>
            
            <div className="mb-2">
                {[...Array(5)].map((_, i) => <span key={i} style={{ color: '#FFC107', fontSize: '14px' }}>★</span>)}
            </div>

            <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '18px', fontWeight: '800', color: '#222' }}>₹{currentPrice.toFixed(2)}</span>
                {currentMRP > 0 && currentMRP > currentPrice && (
                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '13px' }}>₹{currentMRP.toFixed(2)}</span>
                )}
            </div>
          </div>
        </Link>

        {/* FOOTER ACTION SECTION */}
        <div className="p-3 pt-0 mt-auto bg-white border-0">
            <p className="mb-1 text-muted" style={{ fontSize: '11px', fontWeight:'600' }}>PACK SIZE</p>
            
            <div className="d-flex mb-3 flex-wrap gap-1">
                {packs.map(pack => (
                <div key={pack} onClick={() => setSelectedPack(pack)} style={{
                    border: selectedPack === pack ? '1px solid #333' : '1px solid #eee',
                    background: selectedPack === pack ? '#333' : '#fff',
                    color: selectedPack === pack ? '#fff' : '#333',
                    borderRadius: '4px', padding: '6px 0', 
                    flex: packs.length > 3 ? '0 0 30%' : '1', 
                    textAlign: 'center', cursor: 'pointer', fontSize: '11px', fontWeight: '600'
                    }}>
                    x{pack}
                </div>
                ))}
            </div>

            <div className="d-flex justify-content-between align-items-center">
                <div style={{ border: '1px solid #eee', borderRadius: '20px', padding: '4px 10px', display: 'flex', alignItems: 'center' }}>
                    <button onClick={decrement} style={{ border: 'none', background: 'none', padding: '0 5px', fontWeight:'bold' }}>-</button>
                    <span className="mx-2" style={{fontSize: '13px', fontWeight: 'bold'}}>{quantity}</span>
                    <button onClick={increment} style={{ border: 'none', background: 'none', padding: '0 5px', fontWeight:'bold' }}>+</button>
                </div>
                <button onClick={handleAddToCart} disabled={adding} style={{
                    background: adding ? '#eee' : '#FFC35B', border: 'none', borderRadius: '20px',
                    padding: '8px 20px', fontWeight: 'bold', fontSize: '12px', 
                    color: '#222', boxShadow: '0 4px 10px rgba(255, 195, 91, 0.3)'
                }}>
                    {adding ? '...' : 'ADD TO CART'}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

/* ====================================================================
   2. INTERNAL COMPONENT: PRODUCT SLIDER
   ==================================================================== */
const ProductSlider = ({ title, endpoint, bgColor = "#fff" }) => {
  const [products, setProducts] = useState([]);
  const sliderRef = useRef(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get(endpoint);
        setProducts(response.data);
      } catch (error) {
        console.error(`Error fetching ${title}:`, error);
      }
    };
    fetchProducts();
  }, [endpoint, title]);

  const slideLeft = () => { if (sliderRef.current) sliderRef.current.scrollLeft -= 320; };
  const slideRight = () => { if (sliderRef.current) sliderRef.current.scrollLeft += 320; };

  if (!products || products.length === 0) return null;

  return (
    <div style={{ padding: '40px 0', backgroundColor: bgColor, position: 'relative' }}>
      <div style={{ textAlign: 'left', marginBottom: '20px', maxWidth: '1200px', margin: '0 auto 20px auto', paddingLeft: '15px' }}>
        <h2 style={{ fontSize: '32px', fontFamily: 'serif', color: '#333', marginBottom: '5px' }}>{title}</h2>
        <div style={{ width: '60px', height: '3px', backgroundColor: '#FFC35B' }}></div>
      </div>
      <div style={{ maxWidth: '1250px', margin: '0 auto', position: 'relative' }}>
        <button className="hide-on-mobile" onClick={slideLeft} style={{
            position: 'absolute', left: '-15px', top: '50%', transform: 'translateY(-50%)',
            zIndex: 10, backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '50%',
            width: '40px', height: '40px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer'
        }}>&#10094;</button>
        <div ref={sliderRef} style={{ display: 'flex', overflowX: 'auto', scrollBehavior: 'smooth', padding: '10px 0', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {products.map((product) => <ProductCard key={product.productId} product={product} />)}
        </div>
        <button className="hide-on-mobile" onClick={slideRight} style={{
            position: 'absolute', right: '-15px', top: '50%', transform: 'translateY(-50%)',
            zIndex: 10, backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '50%',
            width: '40px', height: '40px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer'
        }}>&#10095;</button>
      </div>
    </div>
  );
};

/* ====================================================================
   3. INTERNAL COMPONENT: MAIN BANNER CAROUSEL (DYNAMIC)
   ==================================================================== */
const MainBannerCarousel = () => {
  const [images, setImages] = useState([]);
  const [current, setCurrent] = useState(0);

  // Fetch images from Backend
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const response = await api.get('/homepage/carousel/get-images');
        setImages(response.data);
      } catch (error) {
        console.error("Error fetching main carousel images:", error);
      }
    };
    fetchImages();
  }, []);

  // Auto-Slide Logic
  useEffect(() => {
    if (images.length <= 1) return;
    const interval = setInterval(() => {
      setCurrent((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const nextSlide = () => setCurrent(current === images.length - 1 ? 0 : current + 1);
  const prevSlide = () => setCurrent(current === 0 ? images.length - 1 : current - 1);

  // FALLBACK: If no images, use your existing Static Banner style
  if (images.length === 0) {
    return (
      <div className="hero-section">
        <p>Welcome to Divine Depot: <br />Your Portal to Spiritual Bliss!</p>
      </div>
    );
  }

  // RENDER DYNAMIC SLIDER
  return (
    // 🟢 UPDATED: Replaced inline styles with the responsive CSS class
    <div className="main-banner-responsive">
      
      {/* Images */}
      {images.map((img, index) => (
        <div
          key={img.id}
          style={{
            position: 'absolute',
            top: 0, left: 0, width: '100%', height: '100%',
            backgroundImage: `url(${img.imageUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: index === current ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            zIndex: index === current ? 1 : 0
          }}
        />
      ))}

      {/* Overlay Text (Optional - keeps consistency) */}
      <div style={{
          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0,0,0,0.2)', zIndex: 2, pointerEvents: 'none'
      }}>
      </div>

      {/* Controls */}
      {images.length > 1 && (
        <>
          <button className="hide-on-mobile" onClick={prevSlide} style={{ ...navBtnStyle, left: '20px' }}>&#10094;</button>
          <button className="hide-on-mobile" onClick={nextSlide} style={{ ...navBtnStyle, right: '20px' }}>&#10095;</button>
          
          <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 10, display: 'flex', gap: '10px' }}>
            {images.map((_, idx) => (
              <div 
                key={idx} 
                onClick={() => setCurrent(idx)}
                style={{
                  width: '12px', height: '12px', borderRadius: '50%', cursor: 'pointer',
                  backgroundColor: current === idx ? '#FFC35B' : 'rgba(255,255,255,0.5)'
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};;

const navBtnStyle = {
  position: 'absolute', top: '50%', transform: 'translateY(-50%)',
  background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff',
  fontSize: '30px', padding: '10px', cursor: 'pointer', zIndex: 10, borderRadius: '50%'
};

/* ====================================================================
   4. INTERNAL COMPONENT: TESTIMONIALS SLIDER (DYNAMIC)
   ==================================================================== */
   const TestimonialsSection = () => {
    // UPDATED: Now fetches data dynamically
    const [testimonials, setTestimonials] = useState([]);
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        const fetchTestimonials = async () => {
            try {
                const response = await api.get('/homepage/testimonials/all');
                setTestimonials(response.data);
            } catch (error) {
                console.error("Error fetching testimonials", error);
            }
        };
        fetchTestimonials();
    }, []);
  
    const length = testimonials.length;
  
    useEffect(() => {
      if(length === 0) return;
      const interval = setInterval(() => {
        setCurrent((prev) => (prev === length - 1 ? 0 : prev + 1));
      }, 5000); 
      return () => clearInterval(interval);
    }, [length]);
  
    const nextSlide = () => setCurrent(current === length - 1 ? 0 : current + 1);
    const prevSlide = () => setCurrent(current === 0 ? length - 1 : current - 1);

    const getCardStyle = (index) => {
        const prevIndex = current === 0 ? length - 1 : current - 1;
        const nextIndex = current === length - 1 ? 0 : current + 1;

        let style = {
            position: 'absolute', top: 0, width: '100%', maxWidth: '500px', height: '100%',
            backgroundColor: '#fff', borderRadius: '20px', padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)', transition: 'all 0.5s ease-in-out',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            opacity: 0, zIndex: 0, transform: 'translateX(-50%) scale(0.8)', left: '50%', pointerEvents: 'none',
        };

        if (index === current) {
            style.opacity = 1; style.zIndex = 10; style.transform = 'translateX(-50%) scale(1)';
            style.pointerEvents = 'auto'; style.boxShadow = '0 15px 40px rgba(255, 195, 91, 0.3)';
        } else if (index === prevIndex) {
            style.opacity = 0.5; style.zIndex = 5; style.transform = 'translateX(-85%) scale(0.85)';
        } else if (index === nextIndex) {
            style.opacity = 0.5; style.zIndex = 5; style.transform = 'translateX(-15%) scale(0.85)';
        } 
        return style;
    };
    
    if (testimonials.length === 0) return null;

    return (
      <div style={{ backgroundColor: '#fffcf7', padding: '60px 0', overflow: 'hidden' }}>
        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h2 style={{ fontSize: '36px', fontFamily: 'serif', color: '#333' }}>What Our Customers Say</h2>
          <div style={{ width: '80px', height: '3px', backgroundColor: '#FFC35B', margin: '10px auto' }}></div>
        </div>
        <div style={{ position: 'relative', height: '420px', maxWidth: '1000px', margin: '0 auto', perspective: '1000px' }}>
            {testimonials.map((item, index) => (
                <div key={item.id} style={getCardStyle(index)}>
                    <div style={{ marginBottom: '20px' }}>
                        {/* UPDATED: Uses customerImageUrl */}
                        <img src={item.customerImageUrl} alt={item.customerName}
                        onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/100?text=User"; }}
                        style={{ width: '90px', height: '90px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #FFC35B' }}
                        />
                    </div>
                    <span style={{ fontSize: '40px', color: '#FFC35B', lineHeight: '10px', marginBottom: '20px' }}>“</span>
                    {/* UPDATED: Uses reviewText */}
                    <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#555', fontStyle: 'italic', textAlign: 'center', marginBottom: '20px' }}>{item.reviewText}</p>
                    {/* UPDATED: Uses customerName */}
                    <h4 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginTop: 'auto' }}>— {item.customerName}</h4>
                </div>
            ))}
            <button className="hide-on-mobile" onClick={prevSlide} style={{ ...arrowBtnStyle, left: '10px' }}>&#10094;</button>
            <button className="hide-on-mobile" onClick={nextSlide} style={{ ...arrowBtnStyle, right: '10px' }}>&#10095;</button>
        </div>
      </div>
    );
  };
  
  const arrowBtnStyle = {
    position: 'absolute', top: '50%', transform: 'translateY(-50%)',
    background: '#fff', border: '1px solid #ddd', borderRadius: '50%',
    width: '45px', height: '45px', fontSize: '20px', color: '#333', cursor: 'pointer',
    zIndex: 20, boxShadow: '0 4px 10px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center'
  };

/* ====================================================================
   5. MAIN BANNER COMPONENT (DEFAULT EXPORT)
   ==================================================================== */

const Banner = () => {
  const [categories, setCategories] = useState([]); 
  const [showAll, setShowAll] = useState(false);
  const [banner, setBanner] = useState(null);
  const [midBanners, setMidBanners] = useState([]);
  const [currentMid, setCurrentMid] = useState(0);
  const [endBanner, setEndBanner] = useState(null); // UPDATED: State for End Banner
  const history = useHistory();

  // 1. Fetch Categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/category/all-unhide'); 
        const categoryList = response.data;
        const formattedCategories = categoryList.map(cat => ({
          name: cat.categoryName, 
          image: cat.categoryImage || "assets/images/Rectangle 7.png" 
        }));
        setCategories(formattedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // 2. Fetch Mid Banner
  // 2. Fetch Mid Banners (Upgraded to Carousel)
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await api.get('/homepage/mid-banners/get-images');
        if (response.data && response.data.length > 0) {
          setMidBanners(response.data); 
        }
      } catch (error) {
        console.error("Error fetching mid banners", error);
      }
    };
    fetchBanners();
  }, []);

  // Timer to auto-slide the Mid Banner every 5 seconds
  useEffect(() => {
    if (midBanners.length <= 1) return; // Don't slide if there's only 1 image
    const interval = setInterval(() => {
      setCurrentMid((prev) => (prev === midBanners.length - 1 ? 0 : prev + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [midBanners.length]);

  // 3. UPDATED: Fetch End Banner
  // 3. UPDATED: Fetch End Banner (DEBUG VERSION)
  useEffect(() => {
      const fetchEndBanner = async () => {
          try {
              console.log("Fetching End Banner..."); // Debug 1
              const response = await api.get(`/homepage/end-banner/get-active?t=${new Date().getTime()}`);
              
              console.log("End Banner API Response:", response.data); // Debug 2: See exactly what the DB returns

              if(response.data && response.data.length > 0){
                  // Sort by ID to get the newest one
                  const sortedBanners = response.data.sort((a, b) => b.id - a.id);
                  console.log("Selected Banner:", sortedBanners[0]); // Debug 3: See which one we picked
                  
                  setEndBanner(sortedBanners[0]); 
              } else {
                  console.warn("API returned an empty list! Showing Default Banner.");
              }
          } catch (error) {
              console.error("Error fetching end banner:", error);
          }
      };
      fetchEndBanner();
  }, []);

  const visibleCategories = showAll ? categories : categories.slice(0, 6);

  return (
    <div className="home-container">
      
      {/* SECTION 1: DYNAMIC CAROUSEL (REPLACES STATIC TEXT) */}
      <MainBannerCarousel />
      
      {/* SECTION 2: CATEGORIES */}
      {/* 🟢 RENAMED class to 'category-section-wrapper' to disconnect from old CSS */}
      <div className="category-section-wrapper" style={{ 
          paddingTop: '50px', 
          paddingBottom: '50px', 
          // 🟢 UPDATED: "Cosmic Latte" - A rich, premium cream that matches gold perfectly
          background: '#FFF8E7', 
          height: 'auto', 
          width: '100%',
          display: 'flex',
          justifyContent: 'center'
      }}>
        <div className="hero-section2" style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            height: 'auto', 
            minHeight: 'unset',
            width: '100%'
        }}>
          
          <div style={{ textAlign: 'center', marginBottom: '40px', zIndex: 1, position: 'relative' }}>
            <h2 style={{ fontSize: '42px', fontFamily: 'serif', color: '#222', marginBottom: '10px', letterSpacing: '1px' }}>Categories</h2>
            <div style={{ width: '100px', height: '3px', backgroundColor: '#FFC35B', margin: '0 auto' }}></div>
          </div>

          <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              justifyContent: 'center', 
              gap: '30px', 
              maxWidth: '1300px', 
              width: '100%', 
              padding: '0 20px' 
          }}>
            {categories.length === 0 && (
                 <p style={{marginTop: '20px', fontSize:'18px', color:'#777', width: '100%', textAlign: 'center'}}>No categories found.</p>
            )}

            {visibleCategories.map((cat, index) => (
              <div key={index}
                onClick={() => history.push(`/category/${cat.name}`)} 
                style={{ 
                    cursor: 'pointer', textAlign: 'center', transition: 'transform 0.3s', 
                    // 🟢 SIZE FIX: Flex-grow ensures they stretch to fill the row
                    flex: '1 1 350px', 
                    maxWidth: '420px', 
                    marginBottom: '15px' 
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-10px)'; 
                    e.currentTarget.style.zIndex = '2';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.zIndex = '1';
                }}
              >
                <div style={{ 
                  backgroundImage: `url("${cat.image}")`, 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center',
                  // 🟢 HEIGHT FIX: Taller (280px) to match the wider width
                  height: '280px', 
                  width: '100%', 
                  borderRadius: '25px', 
                  boxShadow: '0 15px 35px rgba(0,0,0,0.1)', 
                  border: '5px solid #fff', 
                  backgroundColor: '#fff' 
                }} 
                />
                <p style={{ marginTop: '20px', fontWeight: 'bold', fontSize: '24px', color: '#333', fontFamily: 'serif' }}>{cat.name}</p>
              </div>
            ))}
          </div>

          {categories.length > 6 && (
            <div className="view-btn text-center" style={{ marginTop: '40px', marginBottom: '10px' }}>
                <button onClick={() => setShowAll(!showAll)} 
                style={{ padding: '12px 45px', fontSize: '16px', borderRadius: '30px', backgroundColor: '#FFC35B', border: 'none', fontWeight: 'bold', boxShadow: '0 4px 15px rgba(255, 195, 91, 0.4)', color: '#fff', cursor: 'pointer' }}
                >
                {showAll ? "View Less" : "View ALL"}
                </button>
            </div>
          )}
        </div>
      </div>

      {/* SECTION 3: NEW ARRIVALS SLIDER */}
      <ProductSlider title="New Arrivals" endpoint="/products/new-arrivals" bgColor="#ffffff" />

      {/* SECTION 4: MID BANNER CAROUSEL */}
      {midBanners.length > 0 && (
        <div className="mid-banner-responsive" 
          style={{ 
            backgroundImage: `url('${midBanners[currentMid].imageUrl}')`, 
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '350px',
            position: 'relative',
            width: '100%',
            transition: 'background-image 1s ease-in-out' // Smooth crossfade
          }} 
        >
          {/* Dark Overlay */}
          <div style={{ position: 'absolute', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.3)' }}></div>
          
          {/* Text Content */}
          {midBanners[currentMid].bannerText && (
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 2, textAlign: 'center', color: '#fff', padding: '20px', width: '100%' }}>
              <h2 style={{ fontFamily: 'serif', textShadow: '2px 2px 4px rgba(0,0,0,0.6)' }}>
                {midBanners[currentMid].bannerText}
              </h2>
            </div>
          )}
        </div>
      )}

      {/* SECTION 5: DISCOVER TRADITION */}
      <div className="Discovery">
        
        {/* Top Row: Image Left, Text Right */}
        <div className="discovery1">
          <div className="images1"><img src="assets/images/Rectangle 122.png" alt="Tradition" /></div>
          <div className="discovery2">
            <h1 style={{ color: '#fffc5b' }}>Discover Our Tradition</h1>
            <p style={{ color: '#fffc5b' }}>At Divine Depot , we blend age-old traditions with modern craftsmanship to create fragrant wonders. From the heart of India's natural bounty to your home, our agarbattis carry the essence of our heritage. Join us on a journey of scent and soul, where each stick tells a story of tradition, love, and tranquility.</p>
          </div>
        </div>

        {/* Bottom Row: Text Left, Image Right 
            🟢 FIX: Added marginTop to pull this entire row UP and reduce the gap */}
        <div className="discovery1" style={{ marginTop: '-50px', position: 'relative', zIndex: 2 }}>
          <div className="discovery2">
            <p style={{ color: '#fffc5b' }}>But our journey does not end there; it is merely the beginning of a new chapter in the saga of scent. With every breath, our agarbattis evoke memories of a bygone era, transporting you to a realm of serenity and tranquility. Whether it's the gentle whisper of lavender or the exotic allure of patchouli, each fragrance tells a story, a story of love, hope, and devotion.</p>
          </div>
          <div className="images1"><img src="assets/images/Rectangle 123.png" alt="Journey" /></div>
        </div>

      </div>


      {/* SECTION 6: POPULAR PRODUCTS SLIDER */}
      <ProductSlider title="Popular Products" endpoint="/products/popular" bgColor="#fffcf7" />

      {/* SECTION 7: END BANNER */}
      {endBanner ? (
        <div className="radhe" style={{ 
            // 🟢 FIX 1: Quotes added to handle spaces in filenames
            backgroundImage: `url('${endBanner.imageUrl}')`, 
            backgroundSize: 'cover', 
            backgroundPosition: 'center',
            position: 'relative'
        }}>
          {/* 🟢 FIX 2: Only show the dark box IF there is text */}
          {endBanner.bannerText && (
            <div className="radhe-1">
              <p>{endBanner.bannerText}</p>
            </div>
          )}
        </div>
      ) : (
        // Static Fallback
        <div className="radhe" style={{ backgroundImage: "url('/assets/images/default-banner.png')" }}>
            <div className="radhe-1">
              <p>"Whatever you do, make it an offering to Lord - the food you eat, the sacrifices you make, the help you give, and even your suffering."</p>
            </div>
        </div>
      )}

      {/* SECTION 8: TESTIMONIALS */}
      <TestimonialsSection />

    </div>
  );
};

export default Banner;

